/**
 * Compact advisor workbench strip for Film & TV walkthroughs.
 * Shown briefly by default; surfaces proactive routing without dominating the LP story.
 */

import React, { useMemo, useState } from 'react';

export default function AdvisorWorkbenchBrief({
  productionName,
  cases = [],
  summary,
  onFilterReady,
  onFilterAttention,
}) {
  const [expanded, setExpanded] = useState(false);

  const counts = useMemo(() => {
    const ready = cases.filter((c) => String(c.status || '').toLowerCase() === 'ready_to_submit').length;
    const attention = summary?.needs_attention ?? cases.filter((c) => c.needs_attention).length;
    const open = summary?.filtered_total ?? cases.length;
    return { ready, attention, open };
  }, [cases, summary]);

  if (!productionName && counts.open === 0) return null;

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 sm:px-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Advisor workbench</p>
          <p className="text-sm font-semibold text-slate-900 truncate">
            {productionName ? `Operating partner view - ${productionName}` : 'Operating partner view'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Cases appear when a production is added. You are notified when one is ready for review or flagged for attention.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {counts.ready > 0 ? (
            <button
              type="button"
              onClick={onFilterReady}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-50 text-violet-800 border border-violet-100 text-xs font-semibold hover:bg-violet-100 transition-colors"
            >
              <i className="ri-notification-3-line" aria-hidden />
              {counts.ready} ready for review
            </button>
          ) : null}
          {counts.attention > 0 ? (
            <button
              type="button"
              onClick={onFilterAttention}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-900 border border-amber-100 text-xs font-semibold hover:bg-amber-100 transition-colors"
            >
              <i className="ri-alarm-warning-line" aria-hidden />
              {counts.attention} need attention
            </button>
          ) : null}
          <span className="text-xs text-slate-500 tabular-nums px-2">
            {counts.open} open case{counts.open === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 py-1"
          >
            {expanded ? 'Less' : 'Go deeper'}
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="px-4 pb-4 sm:px-5 border-t border-slate-100 pt-3 text-xs text-slate-600 leading-relaxed space-y-2">
          <p>
            Open any case to review imported participant details, documents, and workflow status.
            State-driven routing promotes cases to <strong>Ready to submit</strong> when requirements are met.
          </p>
          <p>
            Mark cases <strong>Needs attention</strong> for follow-up, or <strong>Complete</strong> when verified.
            Automated AI flagging is planned; use manual flags and status transitions for now.
          </p>
        </div>
      ) : null}
    </div>
  );
}
